import {Box, merge, type Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type React from 'react'
import {useEffect, useRef} from 'react'
import {useListViewVariant} from '../ListView/VariantContext'
import {useListItemTitle} from './TitleContext'
import {Selection} from './Selection'
import {testIdProps} from '@github-ui/test-id-props'
import type {HeaderTag} from '../ListView/TitleContext'
import {
  LinkHeader,
  MarkdownHeader,
  StaticHeader,
  type MarkdownHeaderProps,
  type LinkHeaderProps,
  type StaticHeaderProps,
} from './TitleHeader'

export type TitleProps = {
  /**
   * Additional elements to be rendered after the title header element and trailing badge.
   */
  children?: React.ReactNode
  /*
   * Styling for the div tag around the title, if children are included
   */
  containerSx?: BetterSystemStyleObject
} & Partial<MarkdownHeaderProps> &
  LinkHeaderProps &
  Omit<StaticHeaderProps, 'sanitize'>

export type HeaderProps = TitleProps & {
  headerSx?: BetterSystemStyleObject
  anchorSx?: BetterSystemStyleObject
  titleTag: HeaderTag | 'span'
}

const defaultAnchorStyles: BetterSystemStyleObject = {
  '&:hover': {cursor: 'pointer'},
  color: 'fg.default',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}

const defaultHeaderStyles: BetterSystemStyleObject = {
  maxWidth: 'none',
  display: 'inline-flex',
  flexShrink: 1,
  justifyContent: 'space-between',
  fontSize: 2,
  fontWeight: '500',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
}

const compactHeaderStyles: BetterSystemStyleObject = {
  display: 'flex',
  whiteSpace: ['wrap', 'nowrap'],
  fontSize: 1,
  fontWeight: 'normal',
}

const compactContainerStyles: BetterSystemStyleObject = {
  pb: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}
const defaultContainerStyles: BetterSystemStyleObject = {pt: 2, gridArea: 'primary', flexGrow: 2}

function Title({
  value,
  anchorRef: forwardedAnchorRef,
  headingRef,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
  markdownValue,
  containerSx: externalContainerSx,
  headerSx: externalHeaderSx,
  anchorSx: externalAnchorSx,
  linkProps = {} as React.ComponentPropsWithoutRef<typeof Text>,
  tooltip,
  leadingBadge,
  trailingBadge,
}: TitleProps) {
  const {variant} = useListViewVariant()
  const {setTitle, setTitleAction} = useListItemTitle()
  const fallbackAnchorRef = useRef<HTMLAnchorElement>(null)
  const anchorRef = forwardedAnchorRef || fallbackAnchorRef

  useEffect(() => setTitle(value), [setTitle, value])
  useEffect(() => {
    if ((href || onClick) && typeof anchorRef !== 'function' && anchorRef?.current) {
      setTitleAction(() => () => anchorRef?.current?.click())
    }
  }, [anchorRef, href, onClick, setTitleAction])

  const defaultAnchorSx = merge<BetterSystemStyleObject>(defaultAnchorStyles, {
    '&:hover': {
      color: 'accent.fg',
      textDecoration: 'underline',
    },
  })

  const defaultMarkdownAnchorSx = merge<BetterSystemStyleObject>(defaultAnchorStyles, {
    '& > a:hover': {
      color: 'var(--fgColor-accent, var(--color-accent-fg)) !important',
      textDecoration: 'underline',
    },
  })

  const defaultHeaderSx = merge<BetterSystemStyleObject>(defaultHeaderStyles, {
    ...(variant === 'compact' && compactHeaderStyles),
  })
  const headerSx = externalHeaderSx
    ? merge<BetterSystemStyleObject>(defaultHeaderSx, externalHeaderSx)
    : defaultHeaderSx

  const defaultContainerSx = merge<BetterSystemStyleObject>(
    defaultContainerStyles,
    variant === 'compact' ? compactContainerStyles : {},
  )

  const containerSx = externalContainerSx
    ? merge<BetterSystemStyleObject>(defaultContainerSx, externalContainerSx)
    : defaultContainerSx

  const anchorSx = externalAnchorSx
    ? merge<BetterSystemStyleObject>(defaultAnchorSx, externalAnchorSx)
    : defaultAnchorSx

  const markdownAnchorSx = externalAnchorSx
    ? merge<BetterSystemStyleObject>(defaultMarkdownAnchorSx, externalAnchorSx)
    : defaultMarkdownAnchorSx

  const baseHeaderProps = {
    headerSx,
    leadingBadge,
    trailingBadge,
    tooltip,
    headingRef,
  }

  const header = () => {
    if (markdownValue) {
      return (
        <MarkdownHeader
          markdownValue={markdownValue}
          anchorSx={markdownAnchorSx}
          anchorRef={anchorRef}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          linkProps={linkProps}
          {...baseHeaderProps}
        />
      )
    }

    if (href || onClick) {
      return (
        <LinkHeader
          value={value}
          anchorSx={anchorSx}
          anchorRef={anchorRef}
          href={href}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          linkProps={linkProps}
          {...baseHeaderProps}
        />
      )
    }

    return <StaticHeader value={value} {...baseHeaderProps} />
  }

  return (
    <>
      <Box {...testIdProps('listview-item-title-container')} sx={containerSx}>
        {header()}
        {children}
      </Box>
      <Selection />
    </>
  )
}

Title.displayName = 'ListItem.Title'

export {Title}
